import { FC, useEffect, useMemo, useRef, useState } from "react";
import { VehicleMapDataPoint } from "../../../api/types/VehicleMap";
import svgIcons from "../../../assets/svg/svgIcons";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
} from "../../../utils/dateTime";
import { useLoggedInUserData } from "../../../utils/user";
import {
  SPEED_UNITS_OPTIONS,
  SPEED_UNIT_VALUES,
} from "../../admin-details/account/constants";
import { VehicleGroup } from "../../map-multi-vehicle/useMultiVehicleTripData";
import { StatusCodeFilter } from "../statusCodeFilter";
import { TMapRef } from "../type";
import { ReplayControlsPanel } from "./ReplayControlsPanel";
import { RouteRenderMode, TimedPathPoint } from "./resolveRoutePath";
import { tripKeyFor } from "./renderMultiVehicleRoutes";
import { useMultiVehicleMapRender } from "./useMultiVehicleMapRender";

// what a compare card already has on hand for one trip - device-grouped
// (multi-vehicle) or date-grouped (asset map), doesn't matter here. groupId
// is just an opaque key: whichever axis the caller groups by.
export type DetailMapTrip = {
  groupId: string;
  // short - the asset id, or a date label. feeds the animated route pill.
  groupLabel: string;
  // the account's configured asset label, for the legend and tooltip - only
  // meaningful for vehicle-grouped callers. date-grouped callers (the asset
  // map's own compare view) can leave this out; it falls back to groupLabel.
  groupFullLabel?: string;
  tripNumber: number; // for display only - "Trip 3"
  color: string;
  points: VehicleMapDataPoint[];
};

type Props = {
  title: string;
  trips: DetailMapTrip[];
  timezone: string;
  // which event types get a pin; null means pin everything and an empty
  // array means pin nothing. the compare view above owns this, so the cards
  // and the expanded map agree.
  filteredStatusCodes?: StatusCodeFilter;
  // road-aligned paths the compare view has already resolved. Shared rather
  // than resolved again here, so the cards and this map draw the identical
  // geometry and nothing is fetched twice.
  resolvedPathsRef?: React.MutableRefObject<Map<string, TimedPathPoint[]>>;
  // the Route View the compare above is on. Drives this map's line style so
  // the stroke matches the mode the geometry was resolved with - dotted for
  // Directions, thin for Snap to Roads, dashed for Point to Point.
  routeMode?: RouteRenderMode;
  onClose: () => void;
  onTripPointClick?: (point: VehicleMapDataPoint) => void;
};

// the interactive, full-detail map behind a compare card's "Expand" button
// (or several cards' "View Together") - event pins, click-to-detail, and a
// shared-clock replay, the same machinery the main map uses, driven against
// its own independent google.maps.Map instance rather than the page-level
// singleton BasicMap owns (see plan notes: a second BasicMap isn't safe).
export const TripDetailMap: FC<Props> = ({
  title,
  trips,
  timezone,
  filteredStatusCodes,
  resolvedPathsRef: providedResolvedPathsRef,
  routeMode = "DEFAULT",
  onClose,
  onTripPointClick,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<TMapRef>({
    map: null,
    objects: {
      mTripLayers: [],
      mDirectionsManagers: [],
      mTripsVisibility: [],
      mWaypoints: [],
      mPushpins: [],
      mClusterLayer: null,
      mTripsData: [],
    },
    routeViewsEnabled: {
      DEFAULT: true,
      POINT_TO_POINT: false,
      SNAP_TO_ROAD: false,
      DIRECTIONS_SERVICE: false,
    },
  });

  // the renderer reads the line style off mapRef, so the mode has to be
  // written there before the version bump that makes it re-read
  const [routeViewsVersion, setRouteViewsVersion] = useState(0);
  useEffect(() => {
    mapRef.current.routeViewsEnabled = {
      DEFAULT: routeMode === "DEFAULT",
      POINT_TO_POINT: routeMode === "POINT_TO_POINT",
      SNAP_TO_ROAD: routeMode === "SNAP_TO_ROAD",
      DIRECTIONS_SERVICE: routeMode === "DIRECTIONS_SERVICE",
    };
    setRouteViewsVersion((version) => version + 1);
  }, [routeMode]);
  const [isMapReady, setIsMapReady] = useState(false);

  // a real, interactive map (unlike TripMiniMap's locked-down preview) -
  // its own instance, its own container, created once
  useEffect(() => {
    const google = (window as any).google;
    if (!google || !containerRef.current || mapRef.current.map) return;
    const map = new google.maps.Map(containerRef.current, {
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
    });
    mapRef.current.map = map;
    google.maps.event.addListenerOnce(map, "idle", () => setIsMapReady(true));
  }, []);

  const vehicleGroups: VehicleGroup[] = useMemo(() => {
    const byGroup = new Map<string, VehicleGroup>();
    trips
      .slice()
      .sort((a, b) => a.tripNumber - b.tripNumber)
      .forEach((trip) => {
        const group = byGroup.get(trip.groupId) ?? {
          deviceId: trip.groupId,
          vehicleIndex: byGroup.size,
          label: trip.groupLabel,
          fullLabel: trip.groupFullLabel ?? trip.groupLabel,
          color: trip.color,
          isError: false,
          trips: [],
        };
        group.trips.push(trip.points);
        byGroup.set(trip.groupId, group);
      });
    return Array.from(byGroup.values());
  }, [trips]);

  // every trip passed in is "on the map" - a compare card is already a
  // detail context, unlike the main map where detail is opt-in
  const allTripKeys = useMemo(() => {
    const keys: string[] = [];
    vehicleGroups.forEach((group) =>
      group.trips.forEach((_, tripIndex) =>
        keys.push(tripKeyFor(group.deviceId, tripIndex))
      )
    );
    return keys;
  }, [vehicleGroups]);
  const selectedTripKeys = useMemo(() => new Set(allTripKeys), [allTripKeys]);

  const [hoveredTripKey, setHoveredTripKey] = useState<string | null>(null);
  // the compare view above owns the resolving; this falls back to an empty
  // map only when mounted without one, in which case it draws raw fixes
  const fallbackResolvedPathsRef = useRef<Map<string, TimedPathPoint[]>>(
    new Map()
  );
  const resolvedPathsRef =
    providedResolvedPathsRef ?? fallbackResolvedPathsRef;

  // the account's configured speed unit, for the hover tooltip
  const orgSpeedUnitValue = useLoggedInUserData(
    "speedUnits"
  ) as SPEED_UNIT_VALUES;
  const speedUnit =
    SPEED_UNITS_OPTIONS.find((option) => option.value === orgSpeedUnitValue)
      ?.unit ?? SPEED_UNITS_OPTIONS[0].unit;

  const mapRender = useMultiVehicleMapRender({
    mapRef,
    vehicleGroups,
    selectedTripKeys,
    resolvedPathsRef,
    isDataReady: isMapReady,
    routeViewsVersion,
    hoveredTripKey,
    detailedTripKeys: allTripKeys,
    filteredStatusCodes,
    // cheap at compare-card scale, and exactly what "overlapped on one map"
    // is asking for - overlapping trips cluster into a badge instead of
    // just stacking colours
    overlapsEnabled: true,
    speedUnit,
    timezone,
    onMapHoverTrip: setHoveredTripKey,
    onTripPointClick: (_tripKey, point) => onTripPointClick?.(point),
  });

  // detailedTripKeys is non-empty from the very first render (every trip is
  // already "open"), which makes the hook auto-start its detail-view draw-in
  // the instant the map mounts - a slow, 40-second redraw of a route that's
  // about to be snapped to fully-drawn anyway the moment the map goes idle.
  // The two race, and the loser looks like a route that glitches once and
  // then just sits there under a "Drawing route" panel that isn't actually
  // moving. Cancelling it here (same tick, before it's visibly drawn
  // anything) leaves the map exactly like a compare card: everything up at
  // once. The explicit "Replay All" button below is the real animation.
  useEffect(() => {
    mapRender.stopDetailAnimation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The asset map's compare view can hold up to 7 days at once, so a bare
  // time says nothing about which day's 8am you're watching - the main map's
  // clock leads with the date for exactly that reason. The multi-vehicle
  // compare view is always one day, where the date would just be noise. So
  // rather than have each caller declare which it is, ask the trips: only
  // lead with the date when the set actually spans more than one.
  const spansMultipleDays = useMemo(() => {
    const days = new Set<string>();
    trips.forEach((trip) => {
      // a trip can't span more than two dates, so its ends are enough
      [trip.points[0], trip.points[trip.points.length - 1]].forEach((point) => {
        if (!point) return;
        days.add(
          formattedDateTimeBasedOnTimezone(
            dbTimeToJsTime(point.timestamp),
            timezone,
            "yyyy-MM-dd"
          )
        );
      });
    });
    return days.size > 1;
  }, [trips, timezone]);

  const formattedClock = useMemo(
    () =>
      mapRender.detailClock != null
        ? formattedDateTimeBasedOnTimezone(
            dbTimeToJsTime(mapRender.detailClock),
            timezone,
            spansMultipleDays ? "LLL dd · h:mm:ss aa" : "h:mm:ss aa"
          )
        : null,
    [mapRender.detailClock, timezone, spansMultipleDays]
  );

  return (
    <div className="absolute inset-0 z-[90] bg-white flex flex-col">
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 shrink-0">
        <button
          type="button"
          onClick={onClose}
          className="flex items-center gap-2 text-accent-blue-bright font-semibold text-base"
        >
          <img src={svgIcons.ArrowLeftIcon} alt="" className="w-4 h-4" />
          {title}
        </button>
      </div>

      <div className="flex-grow relative">
        <div ref={containerRef} className="w-full h-full" />

        {vehicleGroups.length > 1 && (
          <div className="absolute top-3 left-3 z-[10] bg-white rounded-lg shadow-md p-2 text-xs max-w-[220px] max-h-[40vh] overflow-y-auto space-y-1">
            {vehicleGroups.map((group) => (
              <div key={group.deviceId} className="flex items-center gap-1.5">
                <span
                  className="inline-block size-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: group.color }}
                />
                <span className="truncate">{group.fullLabel}</span>
              </div>
            ))}
          </div>
        )}

        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[10] flex flex-col gap-2 items-center">
          {!mapRender.isDetailAnimating && (
            <button
              type="button"
              onClick={mapRender.replayDetail}
              className="bg-white rounded-lg shadow-md px-4 h-9 text-xs font-medium text-accent-blue-dark hover:bg-accent-blue-paleO66"
            >
              Replay All
            </button>
          )}
          {mapRender.isDetailAnimating && (
            <ReplayControlsPanel
              label={vehicleGroups.length > 1 ? "Replaying routes" : "Replaying route"}
              isPaused={mapRender.isPaused}
              onTogglePause={mapRender.togglePause}
              onStop={mapRender.stopDetailAnimation}
              progressPercent={mapRender.detailProgress}
              formattedClock={formattedClock}
              currentSpeed={mapRender.replaySpeed}
              onSetSpeed={mapRender.setReplaySpeed}
            />
          )}
        </div>
      </div>
    </div>
  );
};
